import { Grid } from "@material-ui/core";
import PageContainer from "../../components/PageContainer";
import { Helmet } from "react-helmet";
import PageTitle from "../../components/Website/PageTitle";
import { Link } from "react-router-dom";
import { NotebookPen, FileText, Circle, Mail, Phone } from "lucide-react";

const AtppBanner = () => {
  return (
    <section aria-label="ATPP banner" className="atpp-banner">
      <div>
        <img
          src={`/atpp-banner.png`}
          alt=""
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
      <div className="atpp-banner__media">
        <img
          src={`${process.env.PUBLIC_URL}/banner.jpg`}
          alt=""
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
    </section>
  );
};

// Main Component
const AtppProgram = () => {
  const whyAtppList = [
    "Strengthen teacher competence",
    "Standardise professional qualifications nationwide",
    "Protect children’s right to quality education",
    "Align Nigeria’s teaching workforce with TRCN standards",
    "Support teachers toward professional licensing rather than disrupt service delivery",
  ];

  const whoIsAtppFor = [
    "Teachers with non-education degrees currently teaching",
    "HND/ND/OND holders currently teaching",
    "ND/OND holders engaged in instruction",
    "Teachers in public, private, TVET, non-formal, ECCDE, and alternative learning settings",
  ];

  const itBenefits = [
    "Teachers seeking career security and professional recognition",
    "School leaders seeking regulatory compliance",
    "State governments strengthening workforce data and standards",
    "Parents seeking improved classroom quality",
  ];

  const domains = [
    "Professional Knowledge",
    "Professional Practice",
    "Professional Values, Ethics, and Conduct",
    "Professional Engagement and Continuous Development",
  ];

  const keyLearningAreas = [
    "Foundations of Education",
    "Educational Psychology",
    "Curriculum and Instruction",
    " Inclusive Education and Safeguarding ",
    "Assessment and Quality Assurance",
    "Digital Literacy and Edtech",
    "Subject Methodology & Specialisation (ECCDE, Primary, Secondary, TVET)",
    "Supervised Teaching Practice (8 weeks)",
  ];

  const graduationRequirements = [
    "Complete required credit units (40 CU for PGDE / 37 CU for PDE)",
    "Pass Teaching Practice",
    "Demonstrate professional conduct",
    "Meet institutional assessment requirements",
    "Sit for and pass the TRCN Professional Qualifying Examination (where applicable)",
  ];

  const applicationSteps = [
    {
      step: "Step 1: Confirm Eligibility",
      description:
        "Ensure you meet academic qualification and classroom experience requirements.",
    },
    {
      step: "Step 2: Register Online",
      description:
        "Visit the official ATPP registration portal, create an account, and upload required documents.",
    },
    {
      step: "Step 3: Verification & Placement",
      description:
        "Your credentials will be verified, and you will be assigned to an accredited institution.",
    },
    {
      step: "Step 4: Begin Learning",
      description:
        "Access course materials via the LMS and attend scheduled contact sessions.",
    },
    {
      step: "Step 5: Teaching Practice",
      description: "Complete your supervised 8-week practicum.",
    },
    {
      step: "Step 6: TRCN Registration",
      description:
        "Upon successful completion, you will be recommended for TRCN licensing.",
    },
  ];

  const safApplyFeatures = [
    "Secure teacher registration and data collection",
    "Eligibility verification and pathway determination",
    "Document upload for verification",
    "Application status tracking",
  ];

  const distinctionFeatures = [
    "ATPP curriculum delivery in modular format",
    "Interactive learning materials and assessments",
    "Progress tracking and performance analytics",
    "Virtual classroom and peer collaboration tools",
  ];

  return (
    <div className="atpp-page">
      <Grid>
        <PageContainer>
          <Helmet>
            <title>Atpp | NTI</title>
          </Helmet>
          {/* <PageTitle title="Accelerated Teacher Professionalisation Pathway (ATPP)" /> */}
          <AtppBanner />

          <div className="container atpp-content" data-aos="fade-up">
            {/* Tagline/Header Section */}
            <div className="program-view-container" style={{ marginTop: "0" }}>
              <h2 className="pca-header">
                Building a Fully Qualified and Licensed Teaching Workforce for
                Nigeria
              </h2>
              <div className="div-underline" />
            </div>

            {/* Welcome Section */}
            <div className="program-card-atpp">
              <h4 className="pca-header">Welcome to ATPP</h4>
              <p className="pca-para">
                The Accelerated Teacher Professionalisation Pathway (ATPP) is a
                national reform initiative designed to qualify, certify, and
                license teachers currently in classrooms without formal
                professional education training. ATPP provides a structured,
                time-bound pathway for teachers to meet the professional
                standards required by the Teachers Registration Council of
                Nigeria (TRCN), while continuing to serve in schools.
              </p>
              <p className="pca-para">
                This is not simply a training programme. It is a national
                professionalisation pathway that strengthens teacher competence,
                safeguards children, and improves learning outcomes across
                Nigeria.
              </p>
            </div>

            {/* Why Was ATPP Created Section */}
            <div className="program-view-container">
              <h2 className="pca-header">Why Was ATPP Created?</h2>
              <div className="div-underline" />
              <p className="pca-para">
                A significant number of teachers in Nigerian classrooms
                currently hold academic qualifications but lack formal
                pedagogical training and professional certification. This
                creates variability in teaching quality, weak professional
                accountability, and gaps in safeguarding practices.
              </p>
              <div
                className="program-card-atpp-blue"
                style={{ marginTop: "1rem" }}
              >
                <h4 className="pca-header-blue">ATPP was created to:</h4>
                <ul className="pca-list">
                  {whyAtppList.map((item, index) => (
                    <li key={index}>
                      <span className="pca-tick">✔</span>
                      <span className="pca-para" style={{ fontSize: "16px" }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
                <p
                  className="pca-para"
                  style={{ fontStyle: "italic", fontWeight: "600" }}
                >
                  ATPP is a supportive pathway and not a punitive measure.
                </p>
              </div>
            </div>

            {/* Who Is ATPP For Section */}
            <div className="program-view-container" id="atpp-who-is-for">
              <h2 className="pca-header">Who Is ATPP For?</h2>
              <div className="div-underline" />
              <div className="grid">
                <div className="program-card-atpp">
                  <h4 className="pca-header">Specifically designed for:</h4>
                  <ul className="pca-list">
                    {whoIsAtppFor.map((item, index) => (
                      <li key={index}>
                        <span className="pca-tick-green">✔</span>
                        <span className="pca-para" style={{ fontSize: "16px" }}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="program-card-atpp-blue">
                  <h4 className="pca-header-blue">It benefits:</h4>
                  <ul className="pca-list">
                    {itBenefits.map((item, index) => (
                      <li key={index}>
                        <span className="pca-tick">✔</span>
                        <span className="pca-para" style={{ fontSize: "16px" }}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* What Will You Learn Section */}
            <div className="program-view-container">
              <h2 className="pca-header">What Will You Learn?</h2>
              <div className="div-underline" />
              <p className="pca-para">
                The ATPP PGDE and PDE programmes are fully aligned with the TRCN
                Professional Standards for Teachers, covering four essential
                domains:
              </p>
              <div className="grid">
                <div className="program-card-atpp">
                  <h4 className="pca-header">Professional Domains</h4>
                  <ul className="pca-list">
                    {domains.map((item, index) => (
                      <li key={index}>
                        <span className="pca-tick-green">✔</span>
                        <span className="pca-para" style={{ fontSize: "16px" }}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="program-card-atpp-blue">
                  <h4 className="pca-header-blue">Key Areas of Learning</h4>
                  <ul className="pca-list">
                    {keyLearningAreas.map((item, index) => (
                      <li key={index}>
                        <span className="pca-tick">✔</span>
                        <span className="pca-para" style={{ fontSize: "16px" }}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Qualification Routes */}
            <div className="program-view-container">
              <h2 className="pca-header">ATPP Qualification Routes</h2>
              <div className="div-underline" />
              <div className="grid">
                <div className="program-card-atpp">
                  <h4 className="pca-header">
                    1. Postgraduate Diploma in Education (PGDE)
                  </h4>
                  <p className="pca-para">
                    <strong>For:</strong> Holders of non-education Bachelor’s
                    degrees or HND holders with a minimum of 12 months classroom
                    experience.
                  </p>
                  <ul className="pca-list">
                    <li>
                      <span className="pca-tick-green">✔</span>{" "}
                      <strong>Duration:</strong> 6 months (4 months hybrid + 2
                      months supervised practice)
                    </li>
                    <li>
                      <span className="pca-tick-green">✔</span>{" "}
                      <strong>Credit Load:</strong> 40 Credit Units
                    </li>
                    <li>
                      <span className="pca-tick-green">✔</span>{" "}
                      <strong>Outcome:</strong> Eligibility for TRCN
                      registration and licensing
                    </li>
                  </ul>
                </div>
                <div className="program-card-atpp-blue">
                  <h4 className="pca-header-blue">
                    2. Professional Diploma in Education (PDE)
                  </h4>
                  <p className="pca-para">
                    <strong>For:</strong> Holders of Ordinary National Diploma
                    (OND) or National Diploma (ND) with a minimum of 12 months
                    classroom experience.
                  </p>
                  <ul className="pca-list">
                    <li>
                      <span className="pca-tick">✔</span>{" "}
                      <strong>Duration:</strong> 6 months (4 months hybrid + 2
                      months supervised practice)
                    </li>
                    <li>
                      <span className="pca-tick">✔</span>{" "}
                      <strong>Credit Load:</strong> 37 Credit Units
                    </li>
                    <li>
                      <span className="pca-tick">✔</span>{" "}
                      <strong>Outcome:</strong> Eligibility for TRCN
                      registration at the appropriate category
                    </li>
                  </ul>
                </div>
              </div>
              <p
                className="pca-para"
                style={{
                  marginTop: "1rem",
                  textAlign: "center",
                  fontWeight: "600",
                }}
              >
                Both programmes are delivered through accredited institutions
                across geopolitical zones in partnership with NTI.
              </p>
            </div>

            {/* Delivery Section */}
            <div className="program-view-container">
              <h2 className="pca-header">How the Programme Is Delivered</h2>
              <div className="div-underline" />
              <div className="program-card-atpp">
                <p className="pca-para">
                  ATPP is delivered through a blended model that ensures
                  standardisation and quality nationwide:
                </p>
                <div className="grid" style={{ marginTop: "1rem" }}>
                  <ul className="pca-list">
                    <li>
                      <span className="pca-tick-green">✔</span> Face-to-face
                      sessions
                    </li>
                    <li>
                      <span className="pca-tick-green">✔</span> Guided online
                      learning via LMS
                    </li>
                  </ul>
                  <ul className="pca-list">
                    <li>
                      <span className="pca-tick-green">✔</span> Supervised
                      school-based practicum
                    </li>
                    <li>
                      <span className="pca-tick-green">✔</span> Mentor-supported
                      reflective practice
                    </li>
                  </ul>
                </div>
                <p
                  className="pca-para"
                  style={{ marginTop: "1rem", fontSize: "16px" }}
                >
                  Each credit unit equals 15 contact hours or equivalent
                  practical workload. All courses are accredited and
                  quality-assured by TRCN.
                </p>
              </div>
            </div>

            {/* Graduation Section */}
            <div className="program-view-container">
              <h2 className="pca-header">Graduation & TRCN Licensing</h2>
              <div className="div-underline" />
              <div className="program-card-atpp-blue">
                <h4 className="pca-header-blue">
                  To graduate and be recommended for TRCN registration,
                  candidates must:
                </h4>
                <ul className="pca-list">
                  {graduationRequirements.map((req, index) => (
                    <li key={index}>
                      <span className="pca-tick">✔</span>
                      <span className="pca-para" style={{ fontSize: "16px" }}>
                        {req}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="pca-para" style={{ fontWeight: "600" }}>
                  Successful candidates will receive TRCN certification and
                  licensing at the appropriate category.
                </p>
              </div>
            </div>

            {/* How to Apply Section */}
            <div className="kellogs">
              <div className="atpp-steps">
                <h3 className="pca-header-blue">How to Apply</h3>
                <ol className="steps-list">
                  {applicationSteps.map((step, index) => (
                    <li key={index}>
                      <span className="step-number">{step.step}:</span>
                      <span className="pca-para" style={{ fontSize: "16px" }}>
                        {step.description}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* CTA Section */}
            <div className="program-view-container program-center">
              <h2 className="pca-header-blue">Take the Next Step</h2>
              <p className="pca-para">
                Your professional recognition matters. Nigeria’s future depends
                on qualified teachers. Register today and begin your pathway to
                professional licensing.
              </p>
              <div
                className="buttons-container"
                style={{ marginTop: "1.5rem" }}
              >
                <a
                  href="https://applicant.safapply.com/nti"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-btn"
                >
                  <Circle size={16} />{" "}
                  <span style={{ marginLeft: "5px" }}>
                    Register [Apply Now]
                  </span>
                </a>
                <a
                  href="#atpp-who-is-for"
                  className="hover-btn-green"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("atpp-who-is-for")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  <Circle size={16} />{" "}
                  <span style={{ marginLeft: "5px" }}>Check eligibility</span>
                </a>
                <a
                  href="/atpp-programme-guide.pdf"
                  download="atpp-programme-guide.pdf"
                  className="hover-btn"
                >
                  <Circle size={16} />{" "}
                  <span style={{ marginLeft: "5px" }}>
                    Download Programme Guide
                  </span>
                </a>
                <Link to="/atpp-terms-and-conditions" className="hover-btn">
                  <FileText size={16} />{" "}
                  <span style={{ marginLeft: "5px" }}>
                    ATPP Terms &amp; Conditions
                  </span>
                </Link>
              </div>
            </div>

            {/* Digital Platforms Section */}
            <div className="program-view-container"></div>
            <div className="atpp-platforms">
              <div
                className="program-card-atpp-blue atpp-platform-card atpp-platform-card--no-bg"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <div className="header-flex">
                  <h4 className="pca-header-blue">Application Portal</h4>
                </div>
                {/* <p className="pca-para">
                  <span>SAFApply</span> is the official portal for the ATPP
                  programme. Teachers use this platform for registration,
                  eligibility verification, and document upload.
                </p> */}
                {/* <ul className="pca-list">
                  {safApplyFeatures.map((feature, index) => (
                    <li key={index}>
                      <span className="pca-tick">✔</span>
                      <span className="pca-para" style={{ fontSize: "15px" }}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul> */}
                <div
                  style={{
                    marginTop: "auto",
                    paddingTop: "1rem",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <a
                    href="https://applicant.safapply.com/nti"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover-btn"
                  >
                    <Circle size={16} />{" "}
                    <span style={{ marginLeft: "5px" }}>Apply Now</span>
                  </a>
                </div>
              </div>
              {/* <div
                className="program-card-atpp"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <div className="header-flex">
                  <GraduationCap className="header-icon-green" size={35} />
                  <h4 className="pca-header">Learning Management System</h4>
                </div>
                <p className="pca-para">
                  <span>Distinction</span> serves as the primary LMS. Teachers
                  access modular content, interactive materials, and track their
                  performance analytics.
                </p>
                <ul className="pca-list">
                  {distinctionFeatures.map((feature, index) => (
                    <li key={index}>
                      <span className="pca-tick-green">✔</span>
                      <span className="pca-para" style={{ fontSize: "15px" }}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: "auto", paddingTop: "1rem" }}>
                  <a
                    href="https://nti.distinction.app/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover-btn-green"
                  >
                    <Circle size={16} />{" "}
                    <span style={{ marginLeft: "5px" }}>
                      Access Distinction
                    </span>
                  </a>
                </div>
              </div> */}
            </div>

            {/* Contacts Section */}
            <div className="contacts-container centered-contacts">
              <div className="contact-item">
                <Mail className="mail-icon" />
                <a href="mailto:atpp-support@nti.edu.ng">
                  atpp-support@nti.edu.ng
                </a>
              </div>
              <div className="contact-item">
                <Phone className="phone-icon" />
                <a href="tel:+2348031234567">+234 803 123 4567</a>
              </div>
            </div>
          </div>
        </PageContainer>
      </Grid>
    </div>
  );
};

export default AtppProgram;
